import { ThemeEvents } from './events';
import { formatMoney } from '../../scripts/utils';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.openAnydayDialog);

    this.closestSection = this.closest('.shopify-section, dialog');
    if (!this.closestSection) return;

    // Use passive event listener for better scroll performance
    this.closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { passive: true });
  }

  disconnectedCallback() {
    if (this.closestSection) {
      this.closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    }
  }

  /**
   * Opens Anyday's popup from our Anyday badge, using the widget in this price block.
   * @param {MouseEvent} event
   */
  openAnydayDialog = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-anyday-trigger]')) return;
    this.querySelector('anyday-price-widget')?.openDialog?.();
  };

  /**
   * Formats a price value according to the shop's currency settings
   * @param {number} price - The price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(price) {
    if (!price && price !== 0) return '';

    // Use Shopify's formatting if available
    if (window.Shopify && typeof formatMoney === 'function') {
      return formatMoney(price);
    }

    // Fallback to Intl.NumberFormat
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(price / 100);
    } catch (e) {
      console.error('Error formatting price:', e);
      return `$${(price / 100).toFixed(2)}`;
    }
  }

  /**
   * Updates the price display based on variant data
   * @param {VariantUpdateEvent} event - The variant update event
   */
  updatePrice = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    // Skip if this update is for a different product
    if (event.detail.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Update price
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (priceContainer) {
      const formattedPrice = this.formatPrice(variant.price);
      let pricePlaceholder = `<span class="price" data-current-price="">${formattedPrice}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedCompareAtPrice = this.formatPrice(variant.compare_at_price);
        pricePlaceholder = `<span role="group">
          <span class="text-foreground-muted line-through">${formattedCompareAtPrice}</span>
        </span>
        <span role="group">
          <span class="text-accent" data-current-price="">${formattedPrice}</span>
        </span>`;
      }
      if (formattedPrice && priceContainer.innerHTML !== pricePlaceholder) {
        priceContainer.innerHTML = pricePlaceholder;
      }
    }

    // Update any price-related attributes or classes
    this.toggleAttribute('data-available', variant.available);
    this.toggleAttribute('data-on-sale', !!variant.compare_at_price && variant.compare_at_price > variant.price);

    // Update installment amounts (Klarna, Anyday, ...)
    this.querySelectorAll('[data-installment-divisor]').forEach((installment) => {
      const divisor = parseInt(installment.dataset.installmentDivisor || '0', 10);
      const amount = installment.querySelector('.installment-amount');
      if (amount && divisor > 1) {
        amount.innerHTML = this.formatPrice(Math.floor(variant.price / divisor));
      }
    });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
